// client/map/managers/silos/SilosManager.ts
import type { Map as MapType, GeoJSONSource } from "maplibre-gl";
import bearing from "@turf/bearing";
import { point } from "@turf/helpers";
import type { MapEventBus } from "../../core/MapEventBus";
import { SOURCES } from "../../sources";

import type { SiloPlatform } from "../../types";
import { SilosSelection } from "./SilosSelection";
import { SilosRenderer } from "./SilosRenderer";
import { getAltitudeInFeet } from "@/client/lib/getAltitudeInFeet";
import { getSpeedInKnots } from "@/client/lib/getSpeedInKnots";

const STALE_AFTER_MS = 60 * 1000 * 60;
const MAX_HISTORY = 240;

type CachedSiloPlatform = SiloPlatform & { lastSeen: number };

export class SilosManager {
  private siloCache = new Map<string, CachedSiloPlatform>();
  private updateTimer: any = null;
  private pendingUpdates = new Set<string>();
  private tracks = new Map<string, [number, number][]>();
  private selection: SilosSelection;
  private renderer: SilosRenderer;

  constructor(
    private map: MapType,
    private eventBus: MapEventBus,
  ) {
    this.selection = new SilosSelection(eventBus);
    this.renderer = new SilosRenderer(map);

    this.setupListeners();
    this.startUpdateLoop();
  }

  private setupListeners() {
    // 🌟 FIX: Ensure internal tracking class registers selection states on bus updates
    this.eventBus.on("silo:selected", ({ silo_id }) => {
      if (!this.selection.isSelected(silo_id)) {
        (this.selection as any).selectAircraft?.(silo_id);
      }
      this.pendingUpdates.add(silo_id);
      this.setMapFeatureState(silo_id, true);
      this.eventBus.emit("silos:selected-silos-updated");
    });

    this.eventBus.on("silo:deselected", ({ silo_id }) => {
      if (silo_id) {
        if (this.selection.isSelected(silo_id)) {
          (this.selection as any).deselectAircraft?.(silo_id);
        }
        this.pendingUpdates.add(silo_id);
        this.setMapFeatureState(silo_id, false);
        this.eventBus.emit("silos:selected-silos-updated");
      }
    });
  }

  /**
   * Directly sets the interactive state inside MapLibre's state engine memory tracking matrix
   */
  private setMapFeatureState(platformId: string, isSelected: boolean) {
    if (!this.map || !this.map.isStyleLoaded()) return;
    this.map.setFeatureState(
      { source: SOURCES.STATIONS, id: platformId },
      { selected: isSelected },
    );
  }

  private startUpdateLoop() {
    this.updateTimer = setInterval(() => {
      if (this.pendingUpdates.size > 0) {
        this.flushUpdates();
      }
    }, 200); // 10 FPS
  }

  /**
   * 🌟 PUBLIC DATA LOOKUP APIs - Used by useSilosSelection hook
   */
  public getSelectedSilos(): SiloPlatform[] {
    const selected: SiloPlatform[] = [];
    for (const [platformId, platform] of this.siloCache) {
      if (this.selection.isSelected(platformId)) {
        selected.push(platform);
      }
    }
    return selected;
  }

  public getAllSiloPlatforms(): SiloPlatform[] {
    return Array.from(this.siloCache.values());
  }

  /**
   * 🌟 PUBLIC SELECTION INTERACTION INTERFACES - Invoked by MapEngine/UI Components
   */
  public selectSilo(stationId: string): void {
    if (!this.selection.isSelected(stationId)) {
      // 1. Commit directly to the internal tracking class memory registry
      (this.selection as any).selectAircraft?.(stationId);

      // 2. IMMEDIATELY sync MapLibre's interactive feature state machine matrix
      this.setMapFeatureState(stationId, true);

      // 3. Queue for next 10 FPS batch rendering source flush
      this.pendingUpdates.add(stationId);

      // 4. Broadcast changes out to both the map engine layers and React states
      this.eventBus.emit("silo:selected", { silo_id: stationId });
      this.eventBus.emit("silos:selected-silos-updated");
    }
  }

  public deselectSilo(stationId: string): void {
    if (this.selection.isSelected(stationId)) {
      // 1. Commit directly to the internal tracking class memory registry
      (this.selection as any).deselectAircraft?.(stationId);

      // 2. IMMEDIATELY clear MapLibre's interactive feature state machine matrix
      this.setMapFeatureState(stationId, false);

      // 3. Queue for next 10 FPS batch rendering source flush
      this.pendingUpdates.add(stationId);

      // 4. Broadcast changes out to both the map engine layers and React states
      this.eventBus.emit("silo:deselected", { silo_id: stationId });
      this.eventBus.emit("silos:selected-silos-updated");
    }
  }

  /**
   * 🌟 PUBLIC BATCH API - Invoked via WebSocket Stream Channels
   */
  async updateSilosBatch(platforms: SiloPlatform[]) {
    const now = Date.now();

    for (const platform of platforms) {
      const platformId = platform.platform_id;

      const cachedPlatform: CachedSiloPlatform = {
        ...platform,
        lastSeen: now,
      };

      this.updateTracks(platformId, [
        platform.position.lon,
        platform.position.lat,
      ]);

      this.siloCache.set(platformId, cachedPlatform);
      this.pendingUpdates.add(platformId);

      // Push state down out to reactive layout context viewports (e.g. tracking Sidebars)
      this.eventBus.emit("data:silo-updated", {
        silo_id: platformId,
        data: platform,
      });
    }

    // Trigger hook stream sync framework loops
    this.eventBus.emit("data:silos-batch-updated");
  }

  removeSilo(platform_id: string) {
    this.siloCache.delete(platform_id);
    this.pendingUpdates.add(platform_id);
    this.tracks.delete(platform_id);

    if (this.selection.isSelected(platform_id)) {
      this.deselectSilo(platform_id);
    }
  }

  private async flushUpdates() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const stationsSource = this.map.getSource(
      SOURCES.STATIONS,
    ) as GeoJSONSource;
    const trailsSource = this.map.getSource(SOURCES.TRACKS) as GeoJSONSource;

    if (!stationsSource || !trailsSource) return;

    const siloFeatures: GeoJSON.Feature[] = [];
    const trailsFeatures: GeoJSON.Feature[] = [];

    for (const [platformId, platform] of this.siloCache) {
      const isSelected = this.selection.isSelected(platformId);

      // Ensure MapLibre state machine mirrors runtime parameters perfectly upon data flushes
      this.setMapFeatureState(platformId, isSelected);

      const hasHeading = Boolean(
        platform.heading && !Number.isNaN(platform.heading),
      );

      const track = this.tracks.get(platformId);

      const computedBearing = hasHeading
        ? platform.heading
        : this.getTrackBearing(track);

      // 🌟 AGGREGATE CALCULATIONS
      const totalCapacity = platform.silos.reduce(
        (acc, s) => acc + s.capacity,
        0,
      );
      const totalInventory = platform.silos.reduce(
        (acc, s) => acc + s.inventory,
        0,
      );

      siloFeatures.push({
        id: platformId,
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [platform.position.lon, platform.position.lat],
        },
        properties: {
          silo_id: platformId,
          isSelected: isSelected, // Backup safety toggle for data expression branches
          name: platform.name ?? `Station-${platformId.substring(0, 5)}`,
          type: platform.type,
          icon: platform.icon,
          altitude_ft: getAltitudeInFeet(platform.altitude_ft),
          heading_deg: computedBearing,
          speed_knots: getSpeedInKnots(platform.speed_knots),
          combat_range_meters: platform.combat_range_meters,
          health_state: platform.health_state,
          capacity: `Capacity: ${totalCapacity}`,
          inventory: `Inventory: ${totalInventory}`,
        },
      });

      if (!track) continue;

      trailsFeatures.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [...track.values()],
        },
        properties: {
          platform_id: platformId,
          color: "white",
          lastSeen: Date.now(),
        },
      });
    }

    try {
      stationsSource.setData({
        type: "FeatureCollection",
        features: siloFeatures,
      });

      trailsSource.setData({
        type: "FeatureCollection",
        features: trailsFeatures,
      });
    } catch (err) {
      console.warn("MapLibre sources updated before map was ready:", err);
      return;
    }

    this.pendingUpdates.clear();
    this.pruneSilos();
  }

  private updateTracks(platform_id: string, position: [number, number]) {
    let history = this.tracks.get(platform_id);

    if (!history) {
      history = [];
      this.tracks.set(platform_id, history);
    }

    history.push(position);

    if (history.length > MAX_HISTORY) {
      history.shift();
    }
  }

  private getTrackBearing(track: [number, number][] | undefined) {
    if (!track || track.length < 3) return 0;

    const values = Array.from(track.values());
    const last = values.at(-1)!;
    const prev = values.at(-3)!;

    const from = point([last[0], last[1]]);
    const to = point([prev[0], prev[1]]);

    return (bearing(to, from) + 360) % 360;
  }

  private pruneSilos() {
    const now = Date.now();

    for (const [platformId, target] of this.siloCache) {
      if (this.selection.isSelected(platformId)) continue;
      const lastSeen = target.lastSeen ?? 0;

      if (now - lastSeen > STALE_AFTER_MS) {
        this.removeSilo(platformId);
      }
    }
  }

  destroy() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
    }
    this.selection.destroy();
    this.renderer.destroy();
    this.siloCache.clear();
    this.pendingUpdates.clear();
    this.tracks.clear();
  }
}
